// Ekran 06 — olay kaydı drawer'ı (K23 olay bazlı beyan).
//
// Drawer'ın TEK tetikleyicisi gridin olay satırındaki "+ kayıt" düğmesidir
// (ref:636 `openDrawer('olay', r.ol)`): tip ve ünite tetikleyici satırdan
// geldiği için gövdede yeniden seçtirilmez. Referansın gövdesi de tek
// `.fgrid` içinde üç alandır — saat · tipe göre değer · neden (ref:699-702).
//
// Başlık tipe göre değişir (ref:697) ve drawer katmanı açılırken veriliyor;
// bu yüzden TANIM haritası dışa açık.

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { DrawerAltlik, DrawerGovde, useDrawer } from '../../components/Drawer'
import { toast } from '../../components/Toast'
import { ApiHatasi } from '../../lib/api'

import { beyan, type OlayTipi } from './api'
import { sayiyaCevir, trSaatNo } from '../../lib/trFormat'

type Tanim = { baslik: string; etiket: string; birim: string; varsayilan: string; nedenler: string[] }

/** ref:693-696 `cfg` haritası — başlık · değer etiketi · varsayılan · nedenler. */
export const TANIM: Record<OlayTipi, Tanim> = {
  mesale: {
    baslik: 'Meşale — yakılan gaz', etiket: 'Yakılan gaz', birim: 'Nm³', varsayilan: '38',
    nedenler: ['Basınç tahliyesi', 'Motor duruşu', 'Bakım'],
  },
  durus: {
    baslik: 'Kojenerasyon duruşu', etiket: 'Süre', birim: 'dk', varsayilan: '45',
    nedenler: ['Planlı bakım', 'Arıza', 'Gaz kalitesi (H₂S)'],
  },
  kesinti: {
    baslik: 'Şebeke kesintisi', etiket: 'Süre', birim: 'dk', varsayilan: '20',
    nedenler: ['Şebeke arızası', 'Planlı kesinti'],
  },
  parti: {
    baslik: 'Hijyenizasyon partisi', etiket: 'Parti miktarı', birim: 't', varsayilan: '2,0',
    nedenler: ['Mezbaha Kat.3', 'Yemekhane atığı'],
  },
  sevk: {
    baslik: 'Sevk kaydı', etiket: 'Miktar', birim: 't', varsayilan: '', nedenler: ['Ürün sevki'],
  },
  calisma: {
    baslik: 'Çalışma kaydı', etiket: 'Süre', birim: 'saat', varsayilan: '', nedenler: ['Normal çalışma'],
  },
}

export default function OlayDrawer({
  tarih,
  tip,
  uniteId,
}: {
  tarih: string
  /** Tetikleyici satırın olay tipi — gövdede değiştirilmez. */
  tip: OlayTipi
  /** Tetikleyici satırın ünitesi; kayda olduğu gibi yazılır. */
  uniteId: string
}) {
  const { kapat } = useDrawer()
  const kuyruk = useQueryClient()
  const t = TANIM[tip]

  // K30'un NOW'u TESİS saatidir (sunucu BeyanOkuma'da öyle hesaplıyor);
  // tarayıcı saati başka dilimdeyse olay yanlış saate düşerdi.
  const [saat, setSaat] = useState(String(trSaatNo()))
  const [deger, setDeger] = useState(t.varsayilan)
  const [neden, setNeden] = useState(t.nedenler[0])

  const kaydet = useMutation({
    mutationFn: () =>
      beyan.olay(tarih, {
        uniteId: uniteId || null,
        tip,
        saat: Number(saat),
        deger: deger.trim() === '' ? null : sayiyaCevir(deger),
        birim: t.birim,
        aciklama: neden,
      }),
    onSuccess: () => {
      kuyruk.invalidateQueries({ queryKey: ['beyan'] })
      toast(`${t.baslik} kaydedildi — ${saat.padStart(2, '0')}:00`)
      kapat()
    },
    onError: (h) => toast(h instanceof ApiHatasi ? h.message : 'Olay kaydedilemedi'),
  })

  return (
    <>
      <DrawerGovde>
        <div className="fgrid">
          <div className="fld">
            <label>Saat</label>
            {/* Referans serbest metin ("12:40") kullanıyor; şema saati tam saat
                tutuyor (§5.1 olay_kaydi.saat int 0-23), o yüzden seçici. */}
            <select className="in mono" value={saat} onChange={(e) => setSaat(e.target.value)}>
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
              ))}
            </select>
          </div>
          <div className="fld">
            <label>{t.etiket} ({t.birim})</label>
            <input
              className="in mono"
              value={deger}
              onChange={(e) => setDeger(e.target.value)}
            />
          </div>
          <div className="fld" style={{ gridColumn: '1/-1' }}>
            <label>Neden / açıklama</label>
            <select className="in" value={neden} onChange={(e) => setNeden(e.target.value)}>
              {t.nedenler.map((n) => <option key={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </DrawerGovde>

      <DrawerAltlik>
        <button className="btn btn-sec" type="button" onClick={() => kapat()}>Vazgeç</button>
        <button
          className="btn btn-pri"
          type="button"
          disabled={kaydet.isPending}
          onClick={() => kaydet.mutate()}
        >
          {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
      </DrawerAltlik>
    </>
  )
}
